import React, { useState, useRef } from 'react';
import SEO from '../../components/SEO';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import PageBanner from '../../components/PageBanner';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import ReCAPTCHA from "react-google-recaptcha";
import { useCountries } from '../../hooks/useCountries';

const JobDetail = () => {
    const { id } = useParams();
    const { toast } = useToast();
    const [isOpen, setIsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [captchaToken, setCaptchaToken] = useState(null);
    const recaptchaRef = useRef(null);
    const fileInputRef = useRef(null);
    const { countries } = useCountries();

    // Mock Data
    const job = {
        title: "Front-end Engineer",
        location: "Oman",
        description: "About Us: At BAIC, we are a dynamic and innovative company specializing in automotive excellence. We are seeking a skilled Front-end Engineer to join our team and help drive our digital presence.\n\nJob Overview: The Front-end Engineer will work closely with design, product, and QA teams to streamline the web development lifecycle, automate processes, and ensure high availability and reliability of our systems.",
        responsibilities: [
            "Develop and maintain high-quality web applications using React.js and modern frontend technologies.",
            "Collaborate with cross-functional teams to define, design, and ship new features.",
            "Ensure the technical feasibility of UI/UX designs.",
            "Optimize applications for maximum speed and scalability."
        ],
        requirements: [
            "Bachelor's degree in Computer Science, Engineering, or a related field.",
            "3+ years of experience in a Front-end development role.",
            "Strong proficiency in JavaScript, HTML5, CSS3, and React.js.",
            "Experience with modern build tools and code versioning tools like Git."
        ]
    };

    const schema = yup.object().shape({
        title: yup.string().required("Title is required"),
        firstName: yup.string().required("First Name is required"),
        lastName: yup.string().required("Last Name is required"),
        phone: yup.string().required("Phone Number is required"),
        nationality: yup.string().required("Nationality is required"),
        jobTitle: yup.string().required("Job Title is required"),
        email: yup.string().email("Invalid email").required("Email is required"),
        message: yup.string().required("Message is required"),
        cv: yup.mixed().required("CV is required").test("fileSize", "File too large", (value) => {
            if (!value || value.length === 0) return false;
            // 5MB limit
            return value[0].size <= 5000000;
        })
    });

    const { register, handleSubmit, control, formState: { errors }, reset, setValue, watch, trigger, setError } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            title: '',
            firstName: '',
            lastName: '',
            phone: '',
            nationality: '',
            jobTitle: job.title,
            email: '',
            message: '',
            countryCode: '+968'
        }
    });

    const selectedNationality = watch("nationality");

    React.useEffect(() => {
        if (selectedNationality) {
            const country = countries.find(c => String(c.id) === String(selectedNationality));
            setValue("countryCode", (country && country.phone_prefix) || "+968");
        } else if (countries.length > 0) {
            const oman = countries.find(c => c.name.toLowerCase() === 'oman');
            if (oman) {
                setValue("nationality", String(oman.id));
                setValue("countryCode", oman.phone_prefix || "+968");
            }
        }
    }, [selectedNationality, countries, setValue]);

    const cvFile = watch("cv");

    const handleCaptchaChange = (token) => {
        setCaptchaToken(token);
    };

    const onSubmit = async (data) => {
        const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
        if (recaptchaSiteKey && !captchaToken) {
            toast({
                title: "Validation Error",
                description: "Please complete the ReCAPTCHA verification.",
                variant: "destructive",
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('title', data.title);
            formData.append('firstName', data.firstName);
            formData.append('lastName', data.lastName);
            formData.append('phone', `${data.countryCode}${data.phone}`);
            formData.append('nationality', data.nationality);
            formData.append('jobTitle', data.jobTitle);
            formData.append('email', data.email);
            formData.append('message', data.message);
            formData.append('cv', data.cv[0]);
            formData.append('recaptcha_token', captchaToken);

            const response = await axios.post('https://admin.baicoman.com/api/job-application-leads', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.status === 200 || response.status === 201) {
                toast({
                    title: "Success",
                    description: "Your application has been submitted successfully.",
                    className: "bg-green-600 text-white border-none"
                });
                reset();
                if (recaptchaSiteKey && recaptchaRef.current) {
                    recaptchaRef.current.reset();
                }
                setCaptchaToken(null);
                setIsOpen(false);
            }
        } catch (error) {
            console.error(error);
            if (error.response && error.response.data && error.response.data.errors) {
                const serverErrors = error.response.data.errors;
                Object.keys(serverErrors).forEach((field) => {
                    setError(field, {
                        type: "server",
                        message: serverErrors[field][0],
                    });
                });
                toast({
                    title: "Validation Error",
                    description: "Please check the form for errors.",
                    variant: "destructive",
                });
            } else {
                toast({
                    title: "Error",
                    description: "Something went wrong. Please try again.",
                    variant: "destructive",
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files;
        if (file) {
            setValue("cv", file);
            trigger("cv");
        }
    };

    return (
        <div className="min-h-screen bg-white pt-20 lg:pt-[120px]">
            <SEO
                title={`${job.title} | BAIC Oman Careers`}
                description={`Apply for the ${job.title} position at BAIC Oman. Join our dynamic team.`}
                canonicalUrl={`https://baicoman.com/careers/${id}`}
            />
            <h1 className="sr-only">{job.title} - BAIC Oman Careers</h1>
            {/* Hero Section */}
            <PageBanner
                image="/assets/images/career/career-heading.png"
                imageAlt="Careers at BAIC"
            >
                <h2 className="text-2xl md:text-7xl font-bold text-white uppercase tracking-tight">CAREERS</h2>
            </PageBanner>

            <div className="max-w-7xl mx-auto px-4 py-16">
                <div className="mb-4 text-gray-500">
                    {job.location}
                </div>
                <h2 className="text-4xl font-bold mb-8 uppercase text-gray-900">{job.title}</h2>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-8 text-gray-700 leading-relaxed">
                        <div className="prose max-w-none">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Description:</h3>
                            <p className="whitespace-pre-line mb-6">{job.description}</p>

                            <h3 className="text-xl font-bold text-gray-900 mb-2">Key Responsibilities:</h3>
                            <ul className="list-disc pl-5 mb-6 space-y-2">
                                {job.responsibilities.map((req, idx) => (
                                    <li key={idx}>{req}</li>
                                ))}
                            </ul>

                            <h3 className="text-xl font-bold text-gray-900 mb-2">Qualifications:</h3>
                            <ul className="list-disc pl-5 mb-6 space-y-2">
                                {job.requirements.map((req, idx) => (
                                    <li key={idx}>{req}</li>
                                ))}
                            </ul>
                        </div>

                        <div className="pt-8">
                            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                                <DialogTrigger asChild>
                                    <Button className="bg-gray-600 hover:bg-gray-700 text-white rounded-full px-8 py-6 text-sm uppercase tracking-widest font-medium">
                                        Apply for this job
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-[600px] bg-[#FFFBF0] border-none max-h-[90vh] overflow-y-auto">
                                    <DialogHeader>
                                        <DialogTitle className="text-2xl text-gray-800 pb-4">Apply for {job.title}</DialogTitle>
                                    </DialogHeader>
                                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 py-4">

                                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                                            <Label className="text-left md:text-right col-span-1 text-gray-500 font-normal">Title</Label>
                                            <div className="col-span-1 md:col-span-3">
                                                <Controller
                                                    control={control}
                                                    name="title"
                                                    render={({ field }) => (
                                                        <Select onValueChange={field.onChange} value={field.value}>
                                                            <SelectTrigger className="w-full bg-transparent border-none shadow-none p-0 h-auto focus:ring-0 justify-between">
                                                                <SelectValue placeholder="Select" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="Mr">Mr.</SelectItem>
                                                                <SelectItem value="Ms">Ms.</SelectItem>
                                                                <SelectItem value="Mrs">Mrs.</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    )}
                                                />
                                                {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                                            <Label className="text-left md:text-right col-span-1 text-gray-500 font-normal self-start pt-3">First Name</Label>
                                            <div className="col-span-1 md:col-span-3">
                                                <Input {...register("firstName")} className="bg-[#F9F0E6] border-none h-12 rounded-2xl" placeholder="Please enter your first name" />
                                                {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName.message}</p>}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                                            <Label className="text-left md:text-right col-span-1 text-gray-500 font-normal self-start pt-3">Last Name</Label>
                                            <div className="col-span-1 md:col-span-3">
                                                <Input {...register("lastName")} className="bg-[#F9F0E6] border-none h-12 rounded-2xl" placeholder="Please enter your last name" />
                                                {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName.message}</p>}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                                            <Label className="text-left md:text-right col-span-1 text-gray-500 font-normal self-start pt-3">Phone Number</Label>
                                            <div className="col-span-1 md:col-span-3 flex flex-col">
                                                <div className="flex gap-2">
                                                    <div className="bg-[#F9F0E6] flex items-center px-4 rounded-2xl h-12 text-gray-700 min-w-[80px] justify-center text-sm font-medium" dir="ltr">
                                                        {watch("countryCode") || "+971"}
                                                    </div>
                                                    <Input {...register("phone")} className="bg-[#F9F0E6] border-none h-12 rounded-2xl" placeholder="Please enter your Phone" />
                                                </div>
                                                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                                            <Label className="text-left md:text-right col-span-1 text-gray-500 font-normal">Nationality</Label>
                                            <div className="col-span-1 md:col-span-3">
                                                <Controller
                                                    control={control}
                                                    name="nationality"
                                                    render={({ field }) => (
                                                        <Select onValueChange={field.onChange} value={field.value}>
                                                            <SelectTrigger className="w-full bg-transparent border-none shadow-none p-0 h-auto focus:ring-0 justify-between font-medium text-black">
                                                                <SelectValue placeholder="Select Nationality" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {countries.map((country, index) => (
                                                                    <SelectItem key={index} value={String(country.id)}>{country.name}</SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    )}
                                                />
                                                {errors.nationality && <p className="text-red-500 text-xs mt-1">{errors.nationality.message}</p>}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                                            <Label className="text-left md:text-right col-span-1 text-gray-500 font-normal self-start pt-3">Job title</Label>
                                            <div className="col-span-1 md:col-span-3">
                                                <Input readOnly {...register("jobTitle")} className="bg-[#F9F0E6] border-none h-12 rounded-2xl" placeholder="Please enter your job title" />
                                                {errors.jobTitle && <p className="text-red-500 text-xs mt-1">{errors.jobTitle.message}</p>}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                                            <Label className="text-left md:text-right col-span-1 text-gray-500 font-normal self-start pt-3">Email</Label>
                                            <div className="col-span-1 md:col-span-3">
                                                <Input {...register("email")} className="bg-[#F9F0E6] border-none h-12 rounded-2xl" placeholder="Please enter your email address" />
                                                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                                            <Label className="text-left md:text-right col-span-1 text-gray-500 font-normal self-start pt-3">Upload CV</Label>
                                            <div className="col-span-1 md:col-span-3 bg-[#F9F0E6] rounded-2xl p-2 flex flex-col justify-center">
                                                <div className="flex items-center gap-3">
                                                    <Button type="button" onClick={() => fileInputRef.current?.click()} className="bg-gray-600 text-white rounded-lg h-10 px-4 text-xs font-normal">Choose File</Button>
                                                    <span className="text-gray-500 text-sm truncate max-w-[200px]">
                                                        {cvFile && cvFile.length > 0 ? cvFile[0].name : "No file chosen"}
                                                    </span>
                                                    <input
                                                        type="file"
                                                        accept=".pdf,.doc,.docx"
                                                        className="hidden"
                                                        ref={fileInputRef}
                                                        onChange={handleFileChange}
                                                    />
                                                </div>
                                                {errors.cv && <p className="text-red-500 text-xs mt-1 ml-1">{errors.cv.message}</p>}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 gap-2 pt-4">
                                            <Label className="text-gray-500 font-normal mb-2">Message to the hiring manager</Label>
                                            <div className="w-full">
                                                <Textarea {...register("message")} className="bg-[#F9F0E6] border-none rounded-2xl min-h-[120px] p-4 placeholder:text-gray-300 w-full" placeholder="Please enter your message" />
                                                {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message.message}</p>}
                                            </div>
                                        </div>

                                        <div className="flex flex-col items-center gap-4 pt-4">
                                            {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                                                <div className="w-full flex flex-col items-center gap-2">
                                                    <ReCAPTCHA
                                                        ref={recaptchaRef}
                                                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                                        onChange={handleCaptchaChange}
                                                    />
                                                    {errors.recaptcha_token && <p className="text-red-500 text-xs">{errors.recaptcha_token.message}</p>}
                                                </div>
                                            )}
                                            <Button disabled={isSubmitting} type="submit" className="bg-black text-white rounded-full px-12 py-6 text-sm uppercase tracking-widest hover:bg-gray-800">
                                                {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Submit"}
                                            </Button>
                                        </div>

                                    </form>
                                </DialogContent>
                            </Dialog>
                        </div>

                    </div>

                    {/* Sidebar / Quick Links */}
                    <div className="hidden lg:block">
                        <div className="fixed right-0 top-1/2 -translate-y-1/2 z-40 hidden xl:block">
                            <div className="bg-gray-900 text-white py-6 px-2 rounded-l-lg writing-vertical-rl text-xs font-bold tracking-widest cursor-pointer hover:bg-black transition-colors">
                                Quick links
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default JobDetail;
